import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/api/client"
import { SkeletonZeilen } from "@/lib/Skeleton"
import { formatZahl } from "@/lib/format"
import { useDocumentTitle } from "@/lib/useDocumentTitle"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { ZaehlerDetailContent } from "@/pages/ZaehlerDetailPage"
import type { Zaehler } from "@/api/types"

/**
 * Öffnet den Zähler im Sheet (Karten-Fähigkeits-Audit §4, Dual-Muster) und
 * spiegelt die Auswahl per `pushState` in die URL — ohne React-Router-Navigation,
 * damit die Liste (und ihr Sheet-Zustand) beim Öffnen/Schließen NICHT unmountet.
 * Deep-Link-Fallback bleibt die eigenständige Route `/zaehler/:id`
 * (`ZaehlerDetailPage`) für direkt aufgerufene URLs.
 */
function setZaehlerUrl(id: number | null) {
  const pfad = id != null ? `/zaehler/${id}` : "/zaehler"
  window.history.pushState(null, "", pfad)
}

export function ZaehlerListPage() {
  useDocumentTitle("Zähler")
  const [nurRelevant, setNurRelevant] = useState(true)
  const [suche, setSuche] = useState("")
  const [offenerZaehler, setOffenerZaehler] = useState<number | null>(null)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["zaehler", nurRelevant],
    queryFn: async () =>
      (
        await api.get<Zaehler[]>("/zaehler", {
          params: nurRelevant ? { relevant: true } : undefined,
        })
      ).data,
  })

  const gefiltert = useMemo(() => {
    if (!data) return data
    const q = suche.trim().toLocaleLowerCase("de")
    if (!q) return data
    return data.filter(
      (z) =>
        z.displayName.toLocaleLowerCase("de").includes(q) ||
        (z.anlageBeschreibung ?? "").toLocaleLowerCase("de").includes(q) ||
        (z.kostenstelleNavision ?? "").toLocaleLowerCase("de").includes(q),
    )
  }, [data, suche])

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Zählerstamm</h1>
          <p className="mt-1 text-sm text-slate-500">
            Haupt-/Unterzähler-Hierarchie, manuelle Erfassung, Anlagen-Lookup (F2)
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={nurRelevant}
            onChange={(e) => setNurRelevant(e.target.checked)}
          />
          nur relevante Zähler
        </label>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <input
          type="search"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Zähler suchen (Name, Anlage, Kostenstelle) …"
          aria-label="Zähler suchen"
          className="h-9 w-full max-w-sm rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60 sm:w-auto"
        />
      </div>

      {isError && (
        <div className="mt-6 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Zähler konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-2 font-semibold">Name</th>
              <th className="px-4 py-2 font-semibold">Übergeordnet</th>
              <th className="px-4 py-2 font-semibold">Anlage</th>
              <th className="px-4 py-2 font-semibold">Kostenstelle</th>
              <th className="px-4 py-2 font-semibold">Letzter Stand</th>
              <th className="px-4 py-2 font-semibold">Relevant</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <SkeletonZeilen zeilen={6} spalten={6} />}
            {gefiltert?.map((z) => (
              <tr key={z.id} className="min-h-10 border-t border-slate-100 hover:bg-slate-50">
                <td className="p-0">
                  <button
                    type="button"
                    onClick={() => {
                      setOffenerZaehler(z.id)
                      setZaehlerUrl(z.id)
                    }}
                    className="block w-full px-4 py-2.5 text-left font-medium text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                  >
                    {z.displayName}
                  </button>
                </td>
                <td className="px-4 py-2.5 text-slate-700">
                  {data?.find((p) => p.id === z.parentZaehlerId)?.displayName ?? "—"}
                </td>
                <td className="px-4 py-2.5 text-slate-700">{z.anlageBeschreibung ?? "—"}</td>
                <td className="px-4 py-2.5 text-slate-700">{z.kostenstelleNavision ?? "—"}</td>
                <td className="px-4 py-2.5 text-slate-700">{formatZahl(z.letzterStand)}</td>
                <td className="px-4 py-2.5">
                  {z.relevant ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">ja</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">nein</span>
                  )}
                </td>
              </tr>
            ))}
            {!isLoading && data?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  Noch keine Zähler angelegt.
                </td>
              </tr>
            )}
            {!isLoading && data && data.length > 0 && gefiltert?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  Keine Zähler gefunden für „{suche}".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Sheet
        open={offenerZaehler != null}
        onOpenChange={(open) => {
          if (!open) {
            setOffenerZaehler(null)
            setZaehlerUrl(null)
          }
        }}
      >
        <SheetContent sheetKey="energie.zaehler-detail" defaultWidth={576} className="overflow-y-auto">
          <SheetHeader className="sr-only">
            <SheetTitle>Zähler-Detail</SheetTitle>
          </SheetHeader>
          {offenerZaehler != null && (
            <ZaehlerDetailContent
              zaehlerId={offenerZaehler}
              onNavigateZaehler={(id) => {
                setOffenerZaehler(id)
                setZaehlerUrl(id)
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
